import React, { useEffect, useState } from 'react';
import { compose } from 'ramda';
import axios from 'axios';
import Dashboard from 'components/Layout/Dashboard/Dashboard';
import withMeta from 'hoc/withMeta';
import api from 'api';
import Store from 'store/store';
import Router from 'components/Router/Router';
import { Button, Loader, RemoteSubmit } from 'components/Base';
import withCaseInfo from 'hoc/withCaseInfo';
import { QuestionType } from 'store/modules/Quesionnare/types';
import { CustomerType } from 'store/modules/Customers/types';
import { Notify, errHelper } from 'utils';
import BottomNavBar from 'components/Cases/BottomNavBar';
import Routes from 'consts/routes';
import Questionnare from 'components/Questionnare/Questionnare';
import QuestionaireForm from '../../components/CustomerQuestionnaire/QuestionaireForm';
import { makeStyles } from '@material-ui/core';
import classnames from 'classnames';
import CircularProgressBar from 'components/Base/CircularProgressBar/CircularProgressBar';
import { useTheme } from '@material-ui/styles';
import { RiskGroupType } from 'store/modules/Firm/types';

const { connect, actions, selectors } = Store;
const { useParams, useHistory } = Router;

const useStyles = makeStyles((theme: any) => {
  return {
    blankFormButton: {
      marginTop: '2rem',
      display: 'flex',
      justifyContent: 'flex-end',
      gap: '1rem',
      '@media (max-width: 1000px)': {
        marginTop: '0.5rem',
        marginLeft: '1rem',
        marginRight: '1rem'
      }
    },
    questionnaire: {
      flexGrow: 1,
      overflow: 'hidden'
    },
    content: {
      position: 'relative',
      width: 'calc(100% - 68px)',
      '@media (max-width: 1000px)': {
        width: '100%'
      },
      '@media (min-width: 1280px)': {
        paddingLeft: '3%',
        paddingRight: '3%'
      },
      '@media (min-width: 1720px)': {
        paddingLeft: '8%',
        paddingRight: '8%'
      },
      '@media (min-width: 1920px)': {
        paddingLeft: '12%',
        paddingRight: '12%'
      }
    },
    primaryText: {
      color: theme.palette.primary.main
    }
  };
});

interface Props {
  setQuestions: (data: QuestionType[]) => void;
  questions: QuestionType[];
  customers: CustomerType[];
  setAnswers: (data: any) => void;
  setCustomersResponses: (data: any) => void;
  userResponses: any;
  isJointed: boolean;
  setIsJoinedCustomer: (data: any) => void;
  organizationData: any;
  advisor: any;
  riskGroup: RiskGroupType;
  caseInfo: any;
  setRiskSummaries: Function;
}

interface QuestionaireValues {
  [key: string]: string;
}

const QuestionnairePage = ({
  setQuestions,
  questions,
  customers,
  setAnswers,
  setCustomersResponses,
  userResponses,
  isJointed,
  setIsJoinedCustomer,
  organizationData,
  advisor,
  riskGroup,
  caseInfo,
  setRiskSummaries
}: Props) => {
  const theme = useTheme() as any;
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingBlank, setIsLoadingBlank] = useState(false);
  const [isLoadingCompleted, setIsLoadingCompleted] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [riskDescriptions, setRiskDescriptions] = useState([] as any);
  const { portfolio } = useParams() as any;
  const history = useHistory();

  const filteredCustomers = customers.filter(
    (customer: CustomerType) => !customer.is_jointed
  );
  const isMoreThanOneCustomer = filteredCustomers.length > 1;
  const jointCustomer = isMoreThanOneCustomer
    ? customers.find((customer: CustomerType) => customer.is_jointed)
    : null;
  const isJointApplicationSelected = (responses: any) => {
    return responses && responses[2] && responses[2].data.results.length
      ? setIsJoinedCustomer(true)
      : setIsJoinedCustomer(false);
  };

  useEffect(() => {
    axios
      .all([
        api.getQuestionnares(),
        api.getRiskDescriptions(),
        ...customers.map((customer: CustomerType) =>
          api.getCustomerQuestionnare({ customerId: customer.id })
        )
      ])
      .then(
        axios.spread((questions, riskDescriptions, ...customerResponses) => {
          setQuestions(questions.data);
          setRiskDescriptions(riskDescriptions.data);

          if (isMoreThanOneCustomer) {
            const data: any = {};
            customers.forEach((customer: CustomerType, index: number) => {
              const result = customerResponses[index].data.results[0];
              data[customer.id] = {
                responses: result ? result.responses : [],
                id: result ? result.id : null,
                risk_tolerance: result ? result.risk_tolerance : null
              };
            });
            setCustomersResponses(data);
          } else {
            const result = customerResponses[0].data.results[0];
            const data = {
              [customers[0].id]: {
                responses: result ? result.responses : [],
                id: result ? result.id : null,
                risk_tolerance: result ? result.risk_tolerance : null
              }
            };

            setCustomersResponses(data);
          }
          isJointApplicationSelected(customerResponses);
          setIsLoading(false);
        })
      )
      .catch(err => {
        errHelper(err);
        setIsLoading(false);
      });
    /* eslint-disable-next-line */
  }, []);

  const handleSingleSubmit = (values: QuestionaireValues) => {
    setIsSaving(true);
    const dataToSave = {
      responses: Object.values(values).map(value => JSON.parse(value)),
      case_id: portfolio
    };
    return api
      .saveQuestionnare({ customerId: customers[0].id, data: dataToSave })
      .then(() => {
        setIsSaving(false);
        Notify.success('Questionnare', 'Questionnaire was saved');
        history.push({
          pathname: `/${portfolio}/risk-summary`,
          state: { refetch: true }
        });
      })
      .catch(err => {
        errHelper(err);
        setIsSaving(false);
      });
  };

  const callRiskSummaries = () => {
    api.getRiskSummaries({ portfolio }).then(response => {
      setRiskSummaries(response.data);
    });
  };

  const handleJointSubmit = ({ questions }: any) => {
    setIsSaving(true);
    const jointedUser = customers.find(c => c.is_jointed);
    const filteredUsers = customers.filter(c => !c.is_jointed);
    const customerA: any[] = [];
    const customerB: any[] = [];

    if (isJointed) {
      const preparedData = questions.map((q: any) => ({
        question_id: q.id,
        response_id: q[`user-${jointedUser?.id}`]
      }));

      const dataToSave = {
        responses: preparedData,
        case_id: portfolio
      };

      return api
        .saveQuestionnare({ customerId: jointedUser?.id, data: dataToSave })
        .then(({ data }) => {
          callRiskSummaries();

          setCustomersResponses({
            [jointedUser?.id as any]: {
              id: data.id,
              responses: dataToSave.responses,
              risk_tolerance: data.risk_tolerance
            }
          });

          const saveData = {
            [filteredUsers[0].id]: data.risk_tolerance,
            [filteredUsers[1].id]: data.risk_tolerance
          };

          setAnswers(saveData);
          setIsSaving(false);
          Notify.success('Questionnaire', `Questionnaires were saved`);
          history.push(`/${portfolio}/risk-summary`);
        })
        .catch(err => {
          errHelper(err);
          setIsSaving(false);
        });
    }

    questions.forEach((q: any) => {
      customerA.push({
        question_id: q.id,
        response_id: q[`user-${filteredUsers[0].id}`]
      });
      customerB.push({
        question_id: q.id,
        response_id: q[`user-${filteredUsers[1].id}`]
      });
    });

    return axios
      .all([
        api.saveQuestionnare({
          customerId: filteredUsers[0].id,
          data: { responses: customerA, case_id: portfolio }
        }),
        api.saveQuestionnare({
          customerId: filteredUsers[1].id,
          data: { responses: customerB, case_id: portfolio }
        })
      ])
      .then(
        axios.spread((resA, resB) => {
          const saveData = {
            [filteredUsers[0].id]: resA.data.risk_tolerance,
            [filteredUsers[1].id]: resB.data.risk_tolerance
          };
          callRiskSummaries();
          setCustomersResponses({
            [filteredUsers[0].id]: {
              id: resA.data.id,
              responses: customerA,
              risk_tolerance: resA.data.risk_tolerance
            },
            [filteredUsers[1].id]: {
              id: resB.data.id,
              responses: customerB,
              risk_tolerance: resB.data.risk_tolerance
            }
          });
          setAnswers(saveData);
          setIsSaving(false);
          Notify.success('Questionnaire', 'Questionnaires were saved');
          history.push(`/${portfolio}/risk-summary`);
        })
      )
      .catch(err => {
        errHelper(err);
        setIsSaving(false);
      });
  };

  const classes = useStyles();

  const downloadQuestionnare = (customerId: number, blank = false) => {
    blank ? setIsLoadingBlank(true) : setIsLoadingCompleted(true);

    const questionnareId: any = userResponses[customerId]?.id;
    if (!questionnareId && !blank) {
      setIsLoadingCompleted(false);
      return Notify.info('Info', 'Please fill out questionnaire');
    }

    const apiCall = blank
      ? api.downloadBlankQuestionnaire({ customerId })
      : api.downloadQuestionnare({
          customerId,
          questionnaireId: userResponses[customers[0].id]?.id,
          questionnaire2Id: null,
          caseId: portfolio,
          images: undefined,
          docType: 'ATR'
        });

    return apiCall
      .then((response: any) => {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const customerData = customers.find(c => c.id === customerId);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute(
          'download',
          `Questionnaire ${customerData?.display_name ?? ''}.pdf`
        );
        document.body.appendChild(link);
        link.click();
        blank ? setIsLoadingBlank(false) : setIsLoadingCompleted(false);
      })
      .catch(err => {
        blank ? setIsLoadingBlank(false) : setIsLoadingCompleted(false);
        errHelper(err);
      });
  };

  const initialData = questions.map((q: QuestionType) => {
    const allUsersAnswers: any = {};

    customers.forEach((c: CustomerType) => {
      const answer = userResponses[c.id]?.responses.find(
        (a: any) => a.question_id === q.id
      );
      allUsersAnswers[`user-${c.id}`] = answer?.response_id ?? '';
    });

    return {
      ...q,
      ...allUsersAnswers
    };
  });

  const calculatedRiskGroup =
    caseInfo.riskProfiler === 'EValue' ? riskGroup : 100;

  const Result = ({
    customerId,
    customerName
  }: {
    customerId?: number;
    customerName?: string;
  }) => {
    const id = customerId ?? customers[0]?.id;
    const response = userResponses[id];
    if (response && response.risk_tolerance) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center'
          }}
        >
          <span style={{ fontWeight: 600 }}>
            Risk tolerance{customerName ? ` - ${customerName}` : ''}
          </span>
          <CircularProgressBar
            value={(response.risk_tolerance / calculatedRiskGroup) * 100}
            display={response.risk_tolerance.toString()}
            color={
              theme.palette.baseColors.charts['asset-allocation'][
                response.risk_tolerance - 1
              ]
            }
          />
          {
            riskDescriptions.find(
              (description: any) =>
                description.risk_level == response.risk_tolerance
            )?.risk_type
          }
        </div>
      );
    }

    return null;
  };

  return (
    <Dashboard>
      {!isMoreThanOneCustomer && (
        <div className={classes.blankFormButton}>
          <Button
            isLoading={isLoadingBlank}
            onClick={() => downloadQuestionnare(customers[0].id, true)}
          >
            Print Blank Form
          </Button>
          {userResponses[customers[0].id]?.id && (
            <Button
              isLoading={isLoadingCompleted}
              onClick={() => downloadQuestionnare(customers[0].id)}
            >
              Print Completed Form
            </Button>
          )}
        </div>
      )}
      <Loader isLoading={isLoading}>
        <div style={{ paddingBottom: '80px', paddingTop: '36px' }}>
          {isMoreThanOneCustomer ? (
            <>
              <Questionnare
                onSubmit={handleJointSubmit}
                initialValues={{ questions: initialData }}
              />
              <div
                style={{
                  marginTop: '2rem',
                  marginBottom: '2rem',
                  display: 'flex',
                  justifyContent: 'center',
                  gap: '1rem'
                }}
              >
                {isJointed ? (
                  <Result
                    customerId={jointCustomer?.id}
                    customerName={jointCustomer?.display_name}
                  />
                ) : (
                  filteredCustomers.map((customer: CustomerType) => (
                    <Result
                      key={customer.id}
                      customerId={customer.id}
                      customerName={customer.display_name}
                    />
                  ))
                )}
              </div>
            </>
          ) : (
            <div className={classnames(classes.content, classes.questionnaire)}>
              <QuestionaireForm
                onSubmit={handleSingleSubmit}
                surveyData={initialData}
                customerName={customers[0].display_name}
                userEmail={advisor.email}
                organization={organizationData}
                userResponses={userResponses[customers[0].id]?.responses}
                Result={<Result />}
                title="Attitude to Risk Questionnaire"
              />
            </div>
          )}
        </div>
        {isMoreThanOneCustomer && (
          <BottomNavBar
            leftButtons={[
              <Button
                style={{ width: '45%', marginLeft: '32px' }}
                key="cancel"
                color="primary"
                onClick={() =>
                  history.push(
                    Routes.RISK_SUMMARY.MAIN.replace(':portfolio', portfolio)
                  )
                }
              >
                Cancel
              </Button>
            ]}
            rightButtons={[
              <RemoteSubmit
                isLoading={isSaving}
                formName="questionnaire"
                key="Calculate risk tolerance button"
              >
                Confirm
              </RemoteSubmit>
            ]}
          />
        )}
      </Loader>
    </Dashboard>
  );
};

export default compose(
  connect(
    (state: any) => ({
      questions: selectors.questionnareSelectors.getQuestions(state),
      customers: selectors.caseSelectors.caseCustomers(state),
      userResponses: selectors.questionnareSelectors.getUserResponses(state),
      isJointed: selectors.questionnareSelectors.isJoinedCustomers(state),
      organizationData: selectors.authSelectors.organizationData(state),
      advisor: selectors.advisorSelectors.getAdvisor(state),
      riskGroup: selectors.firmSelectors.riskGroup(state),
      caseInfo: selectors.caseSelectors.case(state)
    }),
    {
      setQuestions: actions.questionnareActions.setQuestionnares,
      setAnswers: actions.questionnareActions.setAnswers,
      setCustomersResponses: actions.questionnareActions.setCustomersResponses,
      setIsJoinedCustomer: actions.questionnareActions.setIsJoinedCustomer,
      setRiskSummaries: actions.caseActions.setRiskSummaries
    }
  ),
  withMeta,
  withCaseInfo
)(QuestionnairePage);
